class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));
    facetForm.addEventListener('change', this.onSubmitHandler.bind(this));
    facetForm.addEventListener('click', this.onCustomFacetLinkClick.bind(this));
    facetForm.addEventListener('click', this.onCustomSortClick.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static getExclusiveFilterParams() {
    return [
      'filter.p.m.custom.players',
      'filter.p.m.custom.teams',
      'filter.p.m.custom.leagues',
      'filter.p.m.custom.artists',
    ];
  }

  static getShortFilterParamMap() {
    return {
      player: 'filter.p.m.custom.players',
      team: 'filter.p.m.custom.teams',
      league: 'filter.p.m.custom.leagues',
      artist: 'filter.p.m.custom.artists',
    };
  }

  static handleize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  static escapeHTML(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;',
    })[character]);
  }

  static getFilterValueFromElement(element, param) {
    if (!element) return '';

    if (element.tagName === 'OPTION') return element.value;

    const href = element.getAttribute('href');
    if (!href) return '';

    const url = new URL(href, window.location.origin);
    const values = url.searchParams.getAll(param).filter(Boolean);
    return values[values.length - 1] || '';
  }

  static getActiveShortFilter() {
    const shortFilterParamMap = FacetFiltersForm.getShortFilterParamMap();
    const currentParams = new URLSearchParams(window.location.search);

    for (const [shortParam, longParam] of Object.entries(shortFilterParamMap)) {
      const alias = currentParams.get(shortParam);
      if (alias) return { shortParam, longParam, alias };
    }

    return null;
  }

  static hasMainFilterActive(searchParams = window.location.search) {
    const params = new URLSearchParams(searchParams);
    const hasShortFilter = Object.keys(FacetFiltersForm.getShortFilterParamMap()).some((param) => params.has(param));
    const hasExclusiveFilter = FacetFiltersForm.getExclusiveFilterParams().some((param) => params.has(param));

    return hasShortFilter || hasExclusiveFilter;
  }

  static updateBackLinks(searchParams = window.location.search) {
    const isVisible = FacetFiltersForm.hasMainFilterActive(searchParams);

    document.querySelectorAll('.gh-collection-back-link').forEach((link) => {
      link.classList.toggle('is-visible', isVisible);
    });
  }

  static hidesSoldOutProducts() {
    return window.location.pathname.split('/').includes('collections');
  }

  static enforceInStockCollection() {
    if (!FacetFiltersForm.hidesSoldOutProducts()) return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('filter.v.availability') === '1') return;

    params.set('filter.v.availability', '1');
    params.delete('page');

    const query = params.toString();
    history.replaceState(
      { searchParams: query },
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`
    );
    FacetFiltersForm.updateBackLinks(query);
    FacetFiltersForm.renderPage(query, null, false);
  }

  static findShortFilterValue(param, alias) {
    const normalizedAlias = FacetFiltersForm.handleize(alias);
    const candidates = document.querySelectorAll(`[data-gh-filter-param="${param}"]`);

    for (const candidate of candidates) {
      const candidateAlias = candidate.dataset.ghFilterAlias || candidate.textContent;
      if (FacetFiltersForm.handleize(candidateAlias) === normalizedAlias) {
        return FacetFiltersForm.getFilterValueFromElement(candidate, param);
      }
    }

    return '';
  }

  static resolveShortFilterParams() {
    const shortFilterParamMap = FacetFiltersForm.getShortFilterParamMap();
    const currentParams = new URLSearchParams(window.location.search);
    let selectedParam = '';
    let selectedAlias = '';

    Object.entries(shortFilterParamMap).forEach(([shortParam, longParam]) => {
      const alias = currentParams.get(shortParam);
      if (!alias) return;
      selectedParam = longParam;
      selectedAlias = alias;
    });

    if (!selectedParam || !selectedAlias) return;

    const selectedValue = FacetFiltersForm.findShortFilterValue(selectedParam, selectedAlias);
    if (!selectedValue || selectedValue.startsWith('__gh_short:')) return;

    Object.keys(shortFilterParamMap).forEach((shortParam) => currentParams.delete(shortParam));
    FacetFiltersForm.getExclusiveFilterParams().forEach((param) => currentParams.delete(param));
    currentParams.set(selectedParam, selectedValue);
    if (FacetFiltersForm.hidesSoldOutProducts()) {
      currentParams.set('filter.v.availability', '1');
    }
    currentParams.delete('page');

    const resolvedSearchParams = currentParams.toString();
    history.replaceState({ searchParams: resolvedSearchParams }, '', `${window.location.pathname}?${resolvedSearchParams}`);
    FacetFiltersForm.updateBackLinks(resolvedSearchParams);
    FacetFiltersForm.renderPage(resolvedSearchParams, null, false);
  }

  static getShortFilterLabel(activeShortFilter) {
    const candidate = document.querySelector(
      `[data-gh-filter-param="${activeShortFilter.longParam}"][data-gh-filter-alias="${activeShortFilter.alias}"]`
    );

    if (!candidate) return activeShortFilter.alias.replace(/-/g, ' ');
    const label = candidate.querySelector('span');
    return (label || candidate).textContent.trim();
  }

  static promoteActiveFilterItems() {
    return;
  }

  static scrollToCollectionTop(event) {
    if (!event) return;

    const target =
      document.querySelector('.collection-hero') ||
      document.querySelector('.facets-vertical') ||
      document.getElementById('ProductGridContainer');
    if (!target) return;

    if (FacetFiltersForm.scrollAnimationFrame) {
      window.cancelAnimationFrame(FacetFiltersForm.scrollAnimationFrame);
    }

    const headerOffset = 18;
    const targetTop = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    const startTop = window.pageYOffset;
    const distance = targetTop - startTop;
    const duration = 1200;
    let startTime;

    const easeOutQuint = (progress) => 1 - Math.pow(1 - progress, 5);

    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      window.scrollTo(0, startTop + distance * easeOutQuint(progress));

      if (progress < 1) {
        FacetFiltersForm.scrollAnimationFrame = window.requestAnimationFrame(step);
      }
    };

    FacetFiltersForm.scrollAnimationFrame = window.requestAnimationFrame(step);
  }

  static setPendingActiveFilter(event) {
    const link = event?.target?.closest ? event.target.closest('[data-gh-facet-link]') : null;
    if (!link) return;

    FacetFiltersForm.clearExclusiveActiveFilterState();

    const list = link.closest('.gh-custom-facets__list');
    if (list) {
      list.querySelectorAll('.is-active').forEach((element) => element.classList.remove('is-active'));
      link.classList.add('is-active');
    }
  }

  static clearExclusiveActiveFilterState() {
    FacetFiltersForm.getExclusiveFilterParams().forEach((param) => {
      document.querySelectorAll(`[data-gh-filter-param="${param}"].is-active`).forEach((element) => {
        element.classList.remove('is-active');
      });

      document.querySelectorAll(`select [data-gh-filter-param="${param}"]`).forEach((option) => {
        option.selected = false;
      });
    });
  }

  static applyShortFilterState() {
    const activeShortFilter = FacetFiltersForm.getActiveShortFilter();
    if (!activeShortFilter) return;

    const selectedValue = FacetFiltersForm.findShortFilterValue(activeShortFilter.longParam, activeShortFilter.alias);
    if (selectedValue && !selectedValue.startsWith('__gh_short:')) return;

    FacetFiltersForm.clearExclusiveActiveFilterState();

    const selector = `[data-gh-filter-param="${activeShortFilter.longParam}"][data-gh-filter-alias="${activeShortFilter.alias}"]`;
    document.querySelectorAll(selector).forEach((element) => {
      element.classList.add('is-active');

      if (element.tagName === 'OPTION') {
        const select = element.closest('select');
        if (select) select.value = element.value;
      }
    });

    FacetFiltersForm.renderManualEmptyState(FacetFiltersForm.getShortFilterLabel(activeShortFilter));
  }

  static renderManualEmptyState(label) {
    const productGridContainer = document.getElementById('ProductGridContainer');
    const currentGrid = document.getElementById('product-grid');
    if (!productGridContainer || !currentGrid) return;

    const sectionId = currentGrid.dataset.id || FacetFiltersForm.getSections()[0]?.section || '';
    productGridContainer.innerHTML = `
      <div class="collection collection--empty page-width" id="product-grid" data-id="${sectionId}">
        <div class="loading-overlay gradient"></div>
        <div class="gh-collection-empty">
          No products found for ${FacetFiltersForm.escapeHTML(label)}
        </div>
      </div>
    `;

    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    if (countContainer) countContainer.textContent = '0 products';
    if (countContainerDesktop) countContainerDesktop.textContent = '0 products';
  }

  static getChangedParam(event) {
    const customLink = event && event.target && event.target.closest
      ? event.target.closest('[data-gh-facet-link]')
      : null;
    if (customLink && customLink.dataset.ghFilterParam) return customLink.dataset.ghFilterParam;
    return event && event.target && event.target.name ? event.target.name : '';
  }

  static sanitizeSearchParams(searchParams, event) {
    const params = new URLSearchParams(searchParams);
    const changedParam = FacetFiltersForm.getChangedParam(event);
    const customLink = event && event.target && event.target.closest
      ? event.target.closest('[data-gh-facet-link]')
      : null;
    const clickedActiveFilter = customLink ? customLink.classList.contains('is-active') : false;
    const exclusiveFilterParams = FacetFiltersForm.getExclusiveFilterParams();
    const isExclusiveFilter = exclusiveFilterParams.includes(changedParam);
    const isPriceFilter = changedParam.startsWith('filter.v.price');

    if (isExclusiveFilter) {
      const selectedValues = params.getAll(changedParam).filter(Boolean);
      const selectedValue = selectedValues[selectedValues.length - 1];
      const shortFilterParamMap = FacetFiltersForm.getShortFilterParamMap();
      const changedShortParam = Object.entries(shortFilterParamMap).find(([, longParam]) => longParam === changedParam)?.[0];
      const changedShortValue = changedShortParam ? params.get(changedShortParam) : '';

      exclusiveFilterParams.forEach((param) => params.delete(param));

      Object.keys(shortFilterParamMap).forEach((shortParam) => params.delete(shortParam));

      if (selectedValue && selectedValue.startsWith('__gh_short:')) {
        const [, shortParam, alias] = selectedValue.split(':');
        if (shortParam && alias) {
          params.set(shortParam, alias);
        }
      } else if (selectedValue) {
        params.set(changedParam, selectedValue);
      } else if (!clickedActiveFilter && changedShortParam && changedShortValue) {
        params.set(changedShortParam, changedShortValue);
      }
    }

    if (isExclusiveFilter || isPriceFilter) {
      params.delete('page');
    }

    // Collection pagination must always be calculated from in-stock products.
    if (FacetFiltersForm.hidesSoldOutProducts()) {
      params.set('filter.v.availability', '1');
    }

    return params.toString();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form:not(.facets-vertical-sort) .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.applyShortFilterState();
        FacetFiltersForm.promoteActiveFilterItems();
        FacetFiltersForm.updateBackLinks();
        FacetFiltersForm.bindCustomAccordions();
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.applyShortFilterState();
    FacetFiltersForm.promoteActiveFilterItems();
    FacetFiltersForm.updateBackLinks();
    FacetFiltersForm.bindCustomAccordions();
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHTML.getElementById('ProductCount');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (container && sourceCount) {
      container.innerHTML = sourceCount.innerHTML;
    }
    if (container) {
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      if (sourceCount) {
        containerDesktop.innerHTML = sourceCount.innerHTML;
      }
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form:not(.facets-vertical-sort) .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const customFacetsFromFetch = parsedHTML.querySelector('.gh-custom-facets');
    const customFacetsFromDom = document.querySelector('.gh-custom-facets');

    if (customFacetsFromFetch && customFacetsFromDom) {
      customFacetsFromDom.outerHTML = customFacetsFromFetch.outerHTML;
      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);
      return;
    }

    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const currentActiveFacetsElement = document.querySelector(selector);
      if (!activeFacetsElement || !currentActiveFacetsElement) return;
      currentActiveFacetsElement.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const sourceElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!sourceElement || !targetElement) return;
      targetElement.innerHTML = sourceElement.innerHTML;
    });

    const mobileFacetForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileFacetForm) mobileFacetForm.closest('menu-drawer')?.bindEvents();

    FacetFiltersForm.bindCustomSortOutsideClick();
    FacetFiltersForm.bindCustomAccordions();
  }

  static bindCustomSortOutsideClick() {
    if (FacetFiltersForm.customSortOutsideClickBound) return;
    FacetFiltersForm.customSortOutsideClickBound = true;

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-gh-sort]')) return;

      document.querySelectorAll('[data-gh-sort].is-open').forEach((sort) => {
        sort.classList.remove('is-open');
        sort.querySelector('[data-gh-sort-toggle]')?.setAttribute('aria-expanded', 'false');
      });
    });
  }

  static bindStandaloneCustomSorts() {
    if (FacetFiltersForm.standaloneCustomSortBound) return;
    FacetFiltersForm.standaloneCustomSortBound = true;

    document.addEventListener('click', (event) => {
      if (!event.target.closest('.gh-mobile-toolbar [data-gh-sort]')) return;

      const form = document.querySelector('facet-filters-form');
      if (!form || typeof form.onCustomSortClick !== 'function') return;

      form.onCustomSortClick(event);
    });
  }

  static closeMobileCustomFilters() {
    document.body.classList.remove('gh-mobile-filters-open');
    document.querySelectorAll('[id^="shopify-section-"].is-mobile-filters-open').forEach((section) => {
      section.classList.remove('is-mobile-filters-open');
    });
  }

  static getStoredAccordionKey() {
    try {
      return window.sessionStorage.getItem('gh_open_accordion_key') || FacetFiltersForm.lastOpenedAccordionKey;
    } catch {
      return FacetFiltersForm.lastOpenedAccordionKey;
    }
  }

  static setStoredAccordionKey(value) {
    FacetFiltersForm.lastOpenedAccordionKey = value || null;

    try {
      if (value) {
        window.sessionStorage.setItem('gh_open_accordion_key', value);
      } else {
        window.sessionStorage.removeItem('gh_open_accordion_key');
      }
    } catch {
      // Ignore storage errors and keep in-memory fallback.
    }
  }

  static bindCustomAccordions(scope = document) {
    scope.querySelectorAll('.gh-custom-facets__accordion').forEach((accordion) => {
      if (accordion.dataset.ghAccordionBound === 'true') return;
      accordion.dataset.ghAccordionBound = 'true';

      const items = Array.from(accordion.querySelectorAll('.gh-custom-facets__details'));

      const closeItem = (details) => {
        const panel = details.querySelector('.gh-custom-facets__panel');
        if (!panel) {
          details.open = false;
          return;
        }

        panel.style.maxHeight = `${panel.scrollHeight}px`;
        panel.style.opacity = '1';

        requestAnimationFrame(() => {
          panel.style.maxHeight = '0px';
          panel.style.opacity = '0';
        });

        const handleTransitionEnd = (event) => {
          if (event.propertyName !== 'max-height') return;
          details.open = false;
          panel.removeEventListener('transitionend', handleTransitionEnd);
        };

        panel.addEventListener('transitionend', handleTransitionEnd);
      };

      const openItem = (details) => {
        const panel = details.querySelector('.gh-custom-facets__panel');
        details.open = true;
        if (!panel) return;

        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';

        requestAnimationFrame(() => {
          panel.style.maxHeight = `${panel.scrollHeight}px`;
          panel.style.opacity = '1';
        });
      };

      items.forEach((details) => {
        const summary = details.querySelector('.gh-custom-facets__summary');
        const panel = details.querySelector('.gh-custom-facets__panel');
        if (!summary || !panel) return;

        const matchesStoredAccordion = Boolean(
          details.dataset.ghAccordionKey && details.dataset.ghAccordionKey === FacetFiltersForm.getStoredAccordionKey()
        );
        const hasActiveChild = Boolean(details.querySelector('.gh-custom-facets__link.is-active, .gh-custom-facets__view-all.is-active'));

        if (matchesStoredAccordion || hasActiveChild) {
          details.open = true;
        }

        if (details.open) {
          panel.style.maxHeight = `${panel.scrollHeight}px`;
          panel.style.opacity = '1';
        } else {
          panel.style.maxHeight = '0px';
          panel.style.opacity = '0';
        }

        summary.addEventListener('click', (event) => {
          event.preventDefault();

          if (details.open) {
            if (details.dataset.ghAccordionKey === FacetFiltersForm.getStoredAccordionKey()) {
              FacetFiltersForm.setStoredAccordionKey(null);
            }
            closeItem(details);
            return;
          }

          items.forEach((item) => {
            if (item !== details && item.open) closeItem(item);
          });

          FacetFiltersForm.setStoredAccordionKey(details.dataset.ghAccordionKey || null);
          openItem(details);
        });
      });
    });
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
    FacetFiltersForm.updateBackLinks(searchParams);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    const sanitizedSearchParams = FacetFiltersForm.sanitizeSearchParams(searchParams, event);
    FacetFiltersForm.setPendingActiveFilter(event);
    FacetFiltersForm.scrollToCollectionTop(event);
    FacetFiltersForm.renderPage(sanitizedSearchParams, event);
  }

  onCustomFacetLinkClick(event) {
    const link = event.target.closest('[data-gh-facet-link]');
    if (!link) return;

    event.preventDefault();
    const parentAccordion = link.closest('.gh-custom-facets__details[data-gh-accordion-key]');
    if (parentAccordion?.dataset.ghAccordionKey) {
      FacetFiltersForm.setStoredAccordionKey(parentAccordion.dataset.ghAccordionKey);
    }
    const isLeagueAccordionLink = Boolean(link.closest('.gh-custom-facets__accordion'));
    if (!isLeagueAccordionLink) {
      FacetFiltersForm.closeMobileCustomFilters();
    }
    const queryIndex = link.href.indexOf('?');
    const searchParams = queryIndex === -1 ? '' : link.href.slice(queryIndex + 1);
    this.onSubmitForm(searchParams, event);
  }

  onCustomSortClick(event) {
    const sort = event.target.closest('[data-gh-sort]');
    if (!sort) return;

    const toggle = event.target.closest('[data-gh-sort-toggle]');
    if (toggle) {
      event.preventDefault();
      const isOpen = sort.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(isOpen));
      return;
    }

    const option = event.target.closest('[data-gh-sort-option]');
    if (!option) return;

    event.preventDefault();
    const select = sort.querySelector('[data-gh-sort-select]');
    if (!select) return;

    select.value = option.dataset.ghSortOption;
    sort.classList.remove('is-open');
    sort.querySelector('[data-gh-sort-toggle]')?.setAttribute('aria-expanded', 'false');

    const parentForm = select.closest('form');
    if (parentForm) {
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('sort_by', select.value);
    searchParams.delete('page');
    this.onSubmitForm(searchParams.toString(), event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const isMobileForm = event.target.closest('form')?.id === 'FacetFiltersFormMobile';
    const isCustomMobileFilter = window.matchMedia('(max-width: 1023px)').matches && Boolean(event.target.closest('.gh-custom-facets'));

    if (isMobileForm || isCustomMobileFilter) {
      FacetFiltersForm.closeMobileCustomFilters();
    }

    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const params = new URLSearchParams(url);
    if (FacetFiltersForm.hidesSoldOutProducts()) {
      params.set('filter.v.availability', '1');
    }
    params.delete('page');
    FacetFiltersForm.renderPage(params.toString());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.lastOpenedAccordionKey = null;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.enforceInStockCollection();
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.resolveShortFilterParams();
FacetFiltersForm.applyShortFilterState();
FacetFiltersForm.promoteActiveFilterItems();
FacetFiltersForm.updateBackLinks();
FacetFiltersForm.bindCustomSortOutsideClick();
FacetFiltersForm.bindStandaloneCustomSorts();
FacetFiltersForm.bindCustomAccordions();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('.field__input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.querySelectorAll('[data-gh-price-range-min], [data-gh-price-range-max]').forEach((element) => {
      element.addEventListener('input', this.onSliderInput.bind(this));
      element.addEventListener('change', this.onSliderChange.bind(this));
    });
    this.setMinAndMaxValues();
    this.syncSlidersFromInputs();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncSlidersFromInputs();
  }

  onSliderInput(event) {
    event.stopPropagation();
    this.syncInputsFromSliders(event.currentTarget);
  }

  onSliderChange(event) {
    this.syncInputsFromSliders(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.getPriceInputs();
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }

  getPriceInputs() {
    return Array.from(this.querySelectorAll('.field__input'));
  }

  getSliderInputs() {
    return {
      minSlider: this.querySelector('[data-gh-price-range-min]'),
      maxSlider: this.querySelector('[data-gh-price-range-max]'),
    };
  }

  getNumber(value, fallback = 0) {
    const number = Number(String(value || '').replace(/,/g, ''));
    return Number.isFinite(number) ? number : fallback;
  }

  syncInputsFromSliders(changedSlider) {
    const [minInput, maxInput] = this.getPriceInputs();
    const { minSlider, maxSlider } = this.getSliderInputs();
    if (!minInput || !maxInput || !minSlider || !maxSlider) return;

    let minValue = this.getNumber(minSlider.value);
    let maxValue = this.getNumber(maxSlider.value);

    if (minValue > maxValue) {
      if (changedSlider === minSlider) {
        maxValue = minValue;
        maxSlider.value = maxValue;
      } else {
        minValue = maxValue;
        minSlider.value = minValue;
      }
    }

    minInput.value = minValue;
    maxInput.value = maxValue;
    this.updateSliderFill();
  }

  syncSlidersFromInputs() {
    const [minInput, maxInput] = this.getPriceInputs();
    const { minSlider, maxSlider } = this.getSliderInputs();
    if (!minInput || !maxInput || !minSlider || !maxSlider) return;

    const minValue = this.getNumber(minInput.value, this.getNumber(minSlider.min));
    const maxValue = this.getNumber(maxInput.value, this.getNumber(maxSlider.max));
    minSlider.value = minValue;
    maxSlider.value = Math.max(minValue, maxValue);
    this.updateSliderFill();
  }

  updateSliderFill() {
    const fill = this.querySelector('[data-gh-price-fill]');
    const { minSlider, maxSlider } = this.getSliderInputs();
    if (!fill || !minSlider || !maxSlider) return;

    const min = this.getNumber(minSlider.min);
    const max = this.getNumber(minSlider.max, 1);
    const minValue = this.getNumber(minSlider.value);
    const maxValue = this.getNumber(maxSlider.value, max);
    const range = Math.max(max - min, 1);
    const left = ((minValue - min) / range) * 100;
    const right = 100 - ((maxValue - min) / range) * 100;

    fill.style.left = `calc(${left}% + 1.1rem)`;
    fill.style.right = `calc(${right}% + 1.1rem)`;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
